// pages/AdminPanel/AdminTradeIns.jsx
import React, { useEffect, useState } from 'react';
import { useTheme } from '../../components/Theme/ThemeProvider';
import { summaryApi } from '../../common';
import { toast } from 'react-toastify';
import { Package, Eye, X, Check, Filter, User, Calendar } from 'lucide-react';
import { authFetch } from '../../helpers/authFetch';

function AdminTradeIns() {
  const { colors } = useTheme();
  
  const [tradeIns, setTradeIns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedTradeIn, setSelectedTradeIn] = useState(null);
  const [reviewModal, setReviewModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [reviewData, setReviewData] = useState({
    status: '',
    offeredValue: '',
    adminNotes: '',
    rejectionReason: ''
  });

  const fetchTradeIns = async () => {
    try {
      setLoading(true);
      const url = statusFilter 
        ? `${summaryApi.getAllTradeIns.url}?status=${statusFilter}`
        : summaryApi.getAllTradeIns.url;
        
      const response = await authFetch(url, {
        method: summaryApi.getAllTradeIns.method
        
      });

      const data = await response.json();

      if (data.success) {
        setTradeIns(data.data);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Failed to fetch trade-ins');
    } finally {
      setLoading(false);
    }
  };

  const openReviewModal = (tradeIn) => {
    setSelectedTradeIn(tradeIn);
    setReviewData({
      status: tradeIn.status,
      offeredValue: tradeIn.offeredValue || '',
      adminNotes: tradeIn.adminNotes || '',
      rejectionReason: tradeIn.rejectionReason || ''
    });
    setReviewModal(true);
  };

  const handleReview = async () => {
    if (!reviewData.status) {
      toast.error('Please select a status');
      return;
    }

    if (reviewData.status === 'Accepted' && !reviewData.offeredValue) {
      toast.error('Please enter offered value');
      return;
    }

    if (reviewData.status === 'Rejected' && !reviewData.rejectionReason) {
      toast.error('Please enter rejection reason');
      return;
    }

    setProcessing(true);
    try {
      // FIX: Replace :tradeInId placeholder with actual ID
      const url = summaryApi.reviewTradeIn.url.replace(':tradeInId', selectedTradeIn._id);
      
      const response = await authFetch(url, {
        method: summaryApi.reviewTradeIn.method,
        
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reviewData)
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Trade-in reviewed successfully');
        setReviewModal(false);
        fetchTradeIns();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Failed to review trade-in');
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    fetchTradeIns();
  }, [statusFilter]);

  const getStatusColor = (status) => {
    const colors = {
      Pending: '#F59E0B',
      'Under Review': '#3B82F6',
      Accepted: '#10B981',
      Rejected: '#EF4444',
      Completed: '#8B5CF6'
    };
    return colors[status] || '#F59E0B';
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold" style={{ color: colors.text }}>
            Trade-In Requests
          </h1>
          <p style={{ color: colors.textSecondary }}>
            Manage customer trade-in submissions
          </p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <Filter size={20} style={{ color: colors.primary }} />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 rounded-lg outline-none"
          style={{
            backgroundColor: colors.bgSecondary,
            color: colors.text,
            border: `2px solid ${colors.border}`
          }}
        >
          <option value="">All Status</option>
          <option value="Pending">Pending</option>
          <option value="Under Review">Under Review</option>
          <option value="Accepted">Accepted</option>
          <option value="Rejected">Rejected</option>
          <option value="Completed">Completed</option>
        </select>
      </div>

      {/* Trade-Ins List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-transparent" style={{ borderTopColor: colors.primary }} />
        </div>
      ) : tradeIns.length === 0 ? (
        <div
          className="text-center py-16 rounded-xl"
          style={{ backgroundColor: colors.bgSecondary }}
        >
          <Package size={48} className="mx-auto mb-4" style={{ color: colors.textSecondary, opacity: 0.3 }} />
          <p style={{ color: colors.textSecondary }}>No trade-in requests found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {tradeIns.map((tradeIn) => (
            <div
              key={tradeIn._id}
              className="rounded-xl overflow-hidden transition-all hover:shadow-lg"
              style={{
                backgroundColor: colors.bgSecondary,
                border: `1px solid ${colors.border}`
              }}
            >
              {/* Header with Status */}
              <div
                className="p-4 flex items-center justify-between"
                style={{
                  background: `linear-gradient(135deg, ${colors.primary}15 0%, ${colors.accent}15 100%)`
                }}
              >
                <div className="flex items-center gap-3">
                  {tradeIn.user?.profilePic ? (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/${tradeIn.user.profilePic.replace(/\\/g, '/')}`}
                      alt={tradeIn.user.firstName}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                      style={{ backgroundColor: colors.primary }}
                    >
                      {tradeIn.user?.firstName?.charAt(0) || 'U'}
                    </div>
                  )}
                  <div>
                    <p className="font-semibold" style={{ color: colors.text }}>
                      {tradeIn.user?.firstName} {tradeIn.user?.lastName}
                    </p>
                    <p className="text-xs" style={{ color: colors.textSecondary }}>
                      {tradeIn.user?.email}
                    </p>
                  </div>
                </div>
                <span
                  className="px-3 py-1 rounded-full text-xs font-bold"
                  style={{
                    backgroundColor: `${getStatusColor(tradeIn.status)}20`,
                    color: getStatusColor(tradeIn.status)
                  }}
                >
                  {tradeIn.status}
                </span>
              </div>

              {/* Images */}
              <div className="grid grid-cols-4 gap-2 p-4">
                {tradeIn.phoneImages.slice(0, 4).map((image, idx) => (
                  <img
                    key={idx}
                    src={image}
                    alt={`Device ${idx + 1}`}
                    className="w-full h-20 object-cover rounded-lg"
                  />
                ))}
              </div>

              {/* Device Details */}
              <div className="p-4 space-y-3">
                <h3 className="font-bold text-lg" style={{ color: colors.text }}>
                  {tradeIn.phoneBrand} {tradeIn.phoneModel}
                </h3>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span style={{ color: colors.textSecondary }}>Storage:</span>
                    <span className="ml-2 font-semibold" style={{ color: colors.text }}>
                      {tradeIn.storage}
                    </span>
                  </div>
                  <div>
                    <span style={{ color: colors.textSecondary }}>Condition:</span>
                    <span className="ml-2 font-semibold" style={{ color: colors.text }}>
                      {tradeIn.phoneCondition}
                    </span>
                  </div>
                  <div>
                    <span style={{ color: colors.textSecondary }}>Screen:</span>
                    <span className="ml-2 font-semibold" style={{ color: colors.text }}>
                      {tradeIn.screenCondition}
                    </span>
                  </div>
                  <div>
                    <span style={{ color: colors.textSecondary }}>Battery:</span>
                    <span className="ml-2 font-semibold" style={{ color: colors.text }}>
                      {tradeIn.batteryHealth}
                    </span>
                  </div>
                </div>

                {tradeIn.originalPrice && (
                  <div>
                    <span className="text-sm" style={{ color: colors.textSecondary }}>
                      Original Price:
                    </span>
                    <span className="ml-2 font-bold" style={{ color: colors.primary }}>
                      ₦{tradeIn.originalPrice.toLocaleString()}
                    </span>
                  </div>
                )}

                <div
                  className="p-3 rounded-lg text-sm"
                  style={{ backgroundColor: colors.bg }}
                >
                  <p className="font-semibold mb-1" style={{ color: colors.text }}>
                    Description:
                  </p>
                  <p style={{ color: colors.textSecondary }}>{tradeIn.description}</p>
                </div>

                <div className="flex items-center gap-2 text-xs" style={{ color: colors.textSecondary }}>
                  <Calendar size={14} />
                  Submitted {new Date(tradeIn.createdAt).toLocaleDateString()}
                </div>

                {/* Review Button */}
                <button
                  onClick={() => openReviewModal(tradeIn)}
                  className="w-full py-3 rounded-lg font-semibold text-white transition-all hover:scale-105 flex items-center justify-center gap-2"
                  style={{
                    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
                  }}
                >
                  <Eye size={18} />
                  Review Trade-In
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Review Modal */}
      {reviewModal && selectedTradeIn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
          <div
            className="rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto"
            style={{
              backgroundColor: colors.bgSecondary,
              border: `1px solid ${colors.border}`
            }}
          >
            {/* Header */}
            <div
              className="p-6 border-b flex items-center justify-between sticky top-0 z-10"
              style={{
                borderColor: colors.border,
                backgroundColor: colors.bgSecondary,
                background: `linear-gradient(135deg, ${colors.primary}15 0%, ${colors.accent}15 100%)`
              }}
            >
              <h2 className="text-2xl font-bold" style={{ color: colors.text }}>
                Review Trade-In
              </h2>
              <button
                onClick={() => setReviewModal(false)}
                className="p-2 rounded-lg transition-all hover:scale-110"
                style={{
                  backgroundColor: colors.bg,
                  color: colors.primary
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Status Selection */}
              <div>
                <label className="block text-sm font-semibold mb-3" style={{ color: colors.text }}>
                  Review Status *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setReviewData({ ...reviewData, status: 'Under Review' })}
                    className="p-4 rounded-lg font-semibold transition-all hover:scale-105"
                    style={{
                      backgroundColor: reviewData.status === 'Under Review' ? `${colors.primary}20` : colors.bg,
                      color: reviewData.status === 'Under Review' ? colors.primary : colors.text,
                      border: `2px solid ${reviewData.status === 'Under Review' ? colors.primary : colors.border}`
                    }}
                  >
                    Under Review
                  </button>
                  <button
                    onClick={() => setReviewData({ ...reviewData, status: 'Accepted' })}
                    className="p-4 rounded-lg font-semibold transition-all hover:scale-105"
                    style={{
                      backgroundColor: reviewData.status === 'Accepted' ? '#10B98120' : colors.bg,
                      color: reviewData.status === 'Accepted' ? '#10B981' : colors.text,
                      border: `2px solid ${reviewData.status === 'Accepted' ? '#10B981' : colors.border}`
                    }}
                  >
                    Accept
                  </button>
                  <button
                    onClick={() => setReviewData({ ...reviewData, status: 'Rejected' })}
                    className="p-4 rounded-lg font-semibold transition-all hover:scale-105 col-span-2"
                    style={{
                      backgroundColor: reviewData.status === 'Rejected' ? '#EF444420' : colors.bg,
                      color: reviewData.status === 'Rejected' ? '#EF4444' : colors.text,
                      border: `2px solid ${reviewData.status === 'Rejected' ? '#EF4444' : colors.border}`
                    }}
                  >
                    Reject
                  </button>
                </div>
              </div>

              {/* Offered Value */}
              {reviewData.status === 'Accepted' && (
                <div>
                  <label className="block text-sm font-semibold mb-3" style={{ color: colors.text }}>
                    Offered Value (₦) *
                  </label>
                  <input
                    type="number"
                    value={reviewData.offeredValue}
                    onChange={(e) => setReviewData({ ...reviewData, offeredValue: e.target.value })}
                    placeholder="Enter offered value"
                    className="w-full px-4 py-3 rounded-lg outline-none"
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      border: `2px solid ${colors.border}`
                    }}
                  />
                </div>
              )}

              {/* Rejection Reason */}
              {reviewData.status === 'Rejected' && (
                <div>
                  <label className="block text-sm font-semibold mb-3" style={{ color: colors.text }}>
                    Rejection Reason *
                  </label>
                  <textarea
                    value={reviewData.rejectionReason}
                    onChange={(e) => setReviewData({ ...reviewData, rejectionReason: e.target.value })}
                    placeholder="Explain why this trade-in was rejected..."
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg outline-none resize-none"
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      border: `2px solid ${colors.border}`
                    }}
                  />
                </div>
              )}

              {/* Admin Notes */}
              <div>
                <label className="block text-sm font-semibold mb-3" style={{ color: colors.text }}>
                  Admin Notes (Optional)
                </label>
                <textarea
                  value={reviewData.adminNotes}
                  onChange={(e) => setReviewData({ ...reviewData, adminNotes: e.target.value })}
                  placeholder="Add any internal notes..."
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg outline-none resize-none"
                  style={{
                    backgroundColor: colors.bg,
                    color: colors.text,
                    border: `2px solid ${colors.border}`
                  }}
                />
              </div>

              {/* Submit Button */}
              <button
                onClick={handleReview}
                disabled={processing}
                className="w-full py-4 rounded-lg font-bold text-white transition-all hover:scale-105 disabled:opacity-50"
                style={{
                  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
                }}
              >
                {processing ? 'Processing...' : 'Submit Review'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default AdminTradeIns;